import React from 'react';
import dayjs from 'dayjs';
import type { WorkOrder } from '@/services/workOrder.service';
import { getExecutionDisplayInfo } from '@/utils/getExecutionDisplayInfo';
import { EngineCommercialA4Print } from '@/components/print/EngineCommercialPrints';

export type PrintableWorkOrderProps = {
  workOrder: WorkOrder;
  companyLegalName: string;
  companyLogoUrl?: string;
};

function dash(v: unknown): string {
  if (v == null || v === '') return '—';
  return String(v);
}

function formatDate(d: Date | string | undefined | null): string {
  if (d == null) return '';
  const parsed = dayjs(d);
  return parsed.isValid() ? parsed.format('DD MMM YYYY') : '';
}

/** Work order print — shared pagination engine with restored production/execution meta. */
export const PrintableWorkOrder: React.FC<PrintableWorkOrderProps> = ({
  workOrder: wo,
  companyLegalName,
}) => {
  const exec = getExecutionDisplayInfo(wo.execution_scope);
  const progressPct =
    wo.planned_quantity > 0
      ? Math.round((Number(wo.produced_quantity) / Number(wo.planned_quantity)) * 100)
      : 0;
  const supervisor = wo.production_supervisor
    ? [wo.production_supervisor.first_name, wo.production_supervisor.last_name]
        .filter(Boolean)
        .join(' ')
    : '';
  const qualityEng = wo.quality_engineer
    ? [wo.quality_engineer.first_name, wo.quality_engineer.last_name].filter(Boolean).join(' ')
    : '';

  return (
    <EngineCommercialA4Print
      docType="work_order"
      title="WORK ORDER"
      documentNumber={wo.work_order_number || 'WO'}
      partyName={dash(wo.project_name || wo.project?.project_name)}
      introText={companyLegalName}
      metaLines={[
        { label: 'WO No', value: wo.work_order_number || '' },
        { label: 'Status', value: dash(wo.work_order_status) },
        { label: 'Item', value: dash(wo.item_name || wo.item_code) },
        {
          label: 'Planned quantity',
          value: `${wo.planned_quantity} ${wo.uom || ''}`.trim(),
        },
        {
          label: 'Produced quantity',
          value: `${wo.produced_quantity} ${wo.uom || ''} (${progressPct}%)`.trim(),
        },
        { label: 'Project', value: dash(wo.project_name || wo.project?.project_name) },
        { label: 'Customer order', value: dash(wo.sales_order_number) },
        { label: 'Planned start', value: formatDate(wo.planned_start_date) },
        { label: 'Planned end', value: formatDate(wo.planned_end_date) },
        { label: 'Production supervisor', value: supervisor },
        { label: 'Quality engineer', value: qualityEng },
        { label: 'Execution scope', value: exec.scopeLabel },
        { label: 'Execution mode', value: exec.modeLabel },
        { label: 'Completion rule', value: exec.completionLabel },
        { label: 'Delivery rule', value: exec.deliveryLabel },
        { label: 'Procurement rule', value: exec.procurementLabel || '' },
      ].filter((m) => m.value && m.value !== '—')}
      columns={[
        { key: 'sno', label: '#' },
        { key: 'description', label: 'Section' },
        { key: 'detail', label: 'Detail' },
      ]}
      lines={[
        {
          lineNumber: 1,
          description: 'Production item',
          cells: { detail: dash(wo.item_name || wo.item_code) },
        },
        {
          lineNumber: 2,
          description: 'Planned quantity',
          cells: { detail: `${wo.planned_quantity} ${wo.uom || ''}`.trim() },
        },
        ...(wo.client_scope_of_work
          ? [
              {
                lineNumber: 3,
                description: 'Client scope of work',
                cells: { detail: wo.client_scope_of_work },
              },
            ]
          : []),
        ...(wo.client_quality_requirements
          ? [
              {
                lineNumber: 4,
                description: 'Client quality requirements',
                cells: { detail: wo.client_quality_requirements },
              },
            ]
          : []),
      ]}
    />
  );
};

export default PrintableWorkOrder;
